import { ChangeDetectorRef, Component, computed, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { LogService } from '../../../services/log.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Center } from '../../../models/center';
import _ from 'lodash';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Team } from '../../../models/team';
import { Table, TableModule } from 'primeng/table';
import { defaultIfEmpty, finalize, firstValueFrom } from 'rxjs';
import { CenterService } from '../../../services/center.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { ConfirmationService } from 'primeng/api';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { InputTextModule } from "primeng/inputtext";
import { TooltipModule } from 'primeng/tooltip';
import { TeamService } from '../../../services/team.service';
import { Tournament } from '../../../models/tournament';
import { TournamentService } from '../../../services/tournament.service';
import { Player, Student } from '../../../models/student';
import { Popover, PopoverModule } from 'primeng/popover';
import { PlayerService } from '../../../services/player.service';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { environment } from '../../../../environments/environment';
import { MessageModule } from 'primeng/message';
import { GroupService } from '../../../services/group.service';
import { Router } from '@angular/router';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { AuxiliarService } from '../../../services/auxiliar.service';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-tournament-detail-teams',
  imports: [
    AutoCompleteModule,
    ButtonModule,
    CommonModule,
    ConfirmDialogModule,
    ConfirmPopupModule,
    FormsModule,
    InputTextModule,
    MessageModule,
    Popover,
    PopoverModule,
    SelectModule,
    TableModule,
    TooltipModule,
    TranslateModule,
  ],
  providers: [
    ConfirmationService,
  ],
  templateUrl: './tournament-detail-teams.component.html',
  styleUrl: './tournament-detail-teams.component.scss'
})
export class TournamentDetailTeamsComponent implements OnInit {
  @ViewChild('addPlayerPop', { static: true }) addPlayerPop!: Popover;
  @ViewChild('teamsTable', { static: true }) teamsTable!: Table;

  public _ = _;
  public centers: Center[] = [];
  private readonly destroy$ = inject(DestroyRef);
  public editingRowKeys: { [key: string]: boolean } = {};
  public environment = environment;
  public expandedRows: any = {};
  public isEditing: boolean = false;
  public loading: boolean = true;
  public loadingGroups: boolean = false;
  public newPlayer: Player | undefined;
  public newPlayerTeam: Team | undefined;
  private newTeamCount: number = 0;
  public playersAvailable: Player[] = [];
  public playersAvailableFiltered: Player[] = [];
  public teams = computed(() => {
    const filter = this.auxiliarService.searchField();
    return _.filter(this.teamsRaw(), (t: Team) => {
      return _.some(['name', 'center.name', 'center.town', 'center.province', 'playersCount'], (field: string) => {
        return _.includes(_.toLower(_.deburr(_.get(t, field))), filter);
      });
    });
  });
  public teamsCloned: { [id: string]: Team } = {};
  public teamsRaw = signal<Team[]>([]);
  public tournament: Tournament | undefined;

  constructor(
    public authService: AuthService,
    private auxiliarService: AuxiliarService,
    private cdr: ChangeDetectorRef,
    private centerService: CenterService,
    private confirmationService: ConfirmationService,
    private groupService: GroupService,
    private logService: LogService,
    private playerService: PlayerService,
    private router: Router,
    private teamService: TeamService,
    private tournamentService: TournamentService,
    private translate: TranslateService,
  ){}

  async ngOnInit() {
    this.tournament = this.tournamentService.tournament();
    this.centers = await this.getCenters();
    this.getTournamentTeams();
  }

  private async getCenters() {
    try {
      const res: any = await firstValueFrom(
        this.centerService.getCenters().pipe(
          defaultIfEmpty(null),
        )
      );
      this.logService.info('[Tournament] - getCenters', _.get(res, 'body.data'));
      const centers = _.map(_.get(res, 'body.data', []), (center) => {
        return {
          ...center,
          nameAndTown: `${center.name}<br><span class="center-town">${center.town}</span>`,
        };
      });
      return _.orderBy(centers, 'name');
    } catch (err: any) {
      this.logService.error('[Tournament] - getCenters', err, this.translate.instant('error.centers'));
      return [];
    }
  }

  private getTournamentTeams(): void {
    if(!this.tournament?.id) return;
    this.loading = true;
    this.teamService.getTournamentTeams(this.tournament.id)
      .pipe(
        takeUntilDestroyed(this.destroy$),
        finalize(() => { 
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res) => {
          this.logService.info('[Tournament] - getTournamentTeams', _.get(res, 'body'));
          this.teamsRaw.set(_.map(_.get(res, 'body.data', []), (team: Team) => {
            return {
              ...team,
              center: _.find(this.centers, {id: team.centerId}),
              playersCount: team.players?.length
            }
          }));
          this.teamService.setTeamsCount(this.teamsRaw().length);
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.logService.error('[Tournament] - getTournamentTeams', err, this.translate.instant('error.tournament-teams'));
        }
      });
  }

  public addTeam(): void {
    this.isEditing = true;
    const team = {id: `new-${++this.newTeamCount}`};
    this.teamsRaw.set([team, ...this.teamsRaw()])
    this.teamsTable.first = 0;
    this.teamsTable.initRowEdit(team);
    setTimeout(() => {
      this.cdr.detectChanges();
    });
  }

  public deleteTeam(event: Event, team: Team): void {
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant('confirmation.team-delete'),
      icon: 'pi pi-info-circle',
      acceptLabel: this.translate.instant('yes'),
      rejectLabel: this.translate.instant('no'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.teamService.deleteTeam(team).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
          next: (res) => {
            this.logService.info('[Team] - deleteTeam', res, this.translate.instant('success.team-delete'));
            this.getTournamentTeams();
          },
          error: (err) => {
            this.logService.error('[Team] - deleteTeam', err, this.translate.instant('error.team-delete'));
          }
        });
      },
      reject: () => {}
    });
  }

  public onRowEditInit(team: Team): void {
    this.teamsCloned[team.id as string] = { ...team };
    this.isEditing = true;
  }

  public onRowEditSave(team: Team): void {
    if(!this.tournament?.id) return;
    let teamData: Team = {
      centerId: team.center?.id,
      name: team.name,
      tournamentId: this.tournament.id,
    };
    if(!_.includes(team.id, 'new')) {
      _.set(teamData, 'id', team.id);
      this.teamService.updateTeam(teamData).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
        next: (res) => {
          this.logService.info('[Team] - updateTeam', res);
          this.getTournamentTeams();
          this.isEditing = false;
        },
        error: (err) => {
          this.logService.error('[Team] - updateTeam', err, this.translate.instant('error.team-update'));
        }
      });
    } else {
      this.teamService.createTeam(teamData).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
        next: (res) => {
          this.logService.info('[Team] - createTeam', res);
          this.getTournamentTeams();
          this.isEditing = false;
        },
        error: (err) => {
          this.logService.error('[Team] - createTeam', err, this.translate.instant('error.team-create'));
        }
      });
    }
  }

  public onRowEditCancel(team: Team, index: number): void {
    this.teams()[index] = this.teamsCloned[team.id as string];
    if(_.includes(team.id, 'new-')) {
      const teamsRaw = _.filter(this.teamsRaw(), (team) => !_.includes(team.id, 'new-'));
      this.teamsRaw.set(teamsRaw);
    }
    delete this.teamsCloned[team.id as string];
    this.isEditing = false;
  }

  public async toggleAddPlayerPop(event: Event, team: Team) {
    this.newPlayerTeam = team;
    this.newPlayer = undefined;
    this.playersAvailableFiltered = [];
    // get center available players
    if(team.centerId) {
      const students = await this.getCenterStudents(team.centerId);
      if(students?.length) {
        this.playersAvailable = _.filter(students, (student: Student) => {
          return !_.find(_.flatMap(this.teams, 'players'), {studentId: student.id});
        });
      }
    }
    // open pop up
    this.addPlayerPop.toggle(event);
  }

  private async getCenterStudents(centerId: string) {
    try {
      const res: any = await firstValueFrom(
        this.centerService.getCenter(centerId).pipe(
          defaultIfEmpty(null),
        )
      );
      this.logService.info('[Team] - getCenter', _.get(res, 'body'));
      return _.map(_.get(res, 'body.students', []), (student: Student) => {
        return {...student, name: `${student.firstname} ${student.lastname}`};
      });
    } catch (err: any) {
      this.logService.error('[Team] - getCenter', err, this.translate.instant('error.center-students'));
      return [];
    }
  }

  public addPlayer(): void {
    this.addPlayerPop.hide();
    const playerData = {
      studentId: this.newPlayer?.id,
      teamId: this.newPlayerTeam?.id,
    };
    this.playerService.createPlayer(playerData).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
      next: (res) => {
        this.logService.info('[Team] - createPlayer', res, this.translate.instant('success.player-create'));
        this.getTournamentTeams();
      },
      error: (err) => {
        this.logService.error('[Team] - createPlayer', err, this.translate.instant('error.player-create'));
      }
    });
  }

  public deletePlayer(event: Event, player: Player): void {
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant('confirmation.player-delete'),
      icon: 'pi pi-info-circle',
      acceptLabel: this.translate.instant('yes'),
      rejectLabel: this.translate.instant('no'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.playerService.deletePlayer(player).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
          next: (res) => {
            this.logService.info('[Player] - deletePlayer', res, this.translate.instant('success.player-delete'));
            this.getTournamentTeams();
          },
          error: (err) => {
            this.logService.error('[Player] - deletePlayer', err, this.translate.instant('error.player-delete'));
          }
        });
      },
      reject: () => {}
    });
  }

  // filter for players autocomplete in pop up
  public filterPlayers(event: AutoCompleteCompleteEvent): void {
    let query = event.query;
    this.playersAvailableFiltered = _.filter(this.playersAvailable, (player) => {
      return _.includes(_.toLower(_.deburr(_.get(player, 'name'))), _.toLower(_.deburr(query)));
    });
  }

  public createGroups(event: Event): void {
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant('confirmation.groups-create'),
      header: '',
      icon: 'pi pi-info-circle',
      closable: false,
      key: 'groups-create',
      accept: () => {
        if(!this.tournament?.id) return;
        this.loadingGroups = true;
        this.groupService.createTournamentGroups(this.tournament.id)
          .pipe(
            takeUntilDestroyed(this.destroy$),
            finalize(() => { 
              this.loadingGroups = false;
              this.cdr.detectChanges();
            })
          )
          .subscribe({
            next: (res) => {
              this.logService.debug('[Group] - createTournamentGroups', res);
              if(this.tournament?.id) this.tournamentService.getTournament(this.tournament.id).subscribe(() => {
                this.router.navigate(['admin', 'tournaments', this.tournament?.id, 'groups']);
                this.tournamentService.setTournamentSection('groups');
              });
              
            },
            error: (err) => {
              this.logService.error('[Group] - createTournamentGroups', err, this.translate.instant('error.groups-create'));
            }
          });
      },
      reject: () => {}
    });
  }
}
